"use client"

import { Table as TTable } from "@tanstack/react-table"

import { DeductionListJson } from "@/api"
import { Button } from "@/components/ui/button.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx"
import { currencyFormatter } from "@/utils"

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"

function default_deduction_row_amount(row: unknown): number {
  return (row as DeductionListJson).deduction_amount ?? 0
}

/** Sum display amounts for filtered table rows (used by footer total). */
export function sum_filtered_row_amounts<RowData>(
  rows: { original: RowData }[],
  getRowAmount: (row: RowData) => number = default_deduction_row_amount as (row: RowData) => number
): number {
  return rows.reduce((sum, row) => sum + getRowAmount(row.original), 0)
}

/**
 * Accounting footer buckets. `invoices_total` is the invoice face value; `early_pay_discount_total`
 * is the cash discount taken on those early-pay invoices — the part that actually behaves like a
 * deduction. We surface it separately so the invoice face value doesn't get mistaken for a deduction
 * and so `deductions_total + early_pay_discount_total` reconciles to a remittance's "deductions"
 * figure. Repayments / early-pay discount are shown only when present.
 *
 * Prepayments get their own signed bucket: a prepayment is two legs (withheld on one check, applied
 * on another) that net to zero, so the bucket only shows when a leg is open. Before this existed
 * the negative legs fell into `deductions_total` via its sign catch-all while the positive legs
 * were counted nowhere — three balanced UNFI `…PP` pairs read as −$48,429.87 of phantom deductions.
 */
export interface AccountingSplitTotals {
  deductions_total: number
  invoices_total: number
  early_pay_discount_total: number
  repayments_total: number
  prepayments_total: number
  has_repayments: boolean
  has_early_pay_discount: boolean
  deduction_trade_totals?: AccountingDeductionTradeTotals
}

export interface AccountingDeductionTradeTotals {
  trade_total: number
  non_trade_total: number
  unclassified_total: number
}

export function sum_filtered_accounting_totals<RowData>(
  rows: { original: RowData }[],
  getRowAmount: (row: RowData) => number,
  getRowKind: (row: RowData) => string,
  /** Early-pay discount magnitude for an invoice row (signed as a reduction, i.e. negative). */
  getRowEarlyPayDiscount?: (row: RowData) => number,
  getRowDeductionTradeTotals?: (row: RowData) => AccountingDeductionTradeTotals
): AccountingSplitTotals {
  return rows.reduce<AccountingSplitTotals>(
    (acc, row) => {
      const amount = getRowAmount(row.original)
      const kind = getRowKind(row.original)
      if (kind === "repayment") {
        acc.has_repayments = true
        acc.repayments_total += amount
      } else if (kind === "invoice") {
        acc.invoices_total += amount
        const discount = getRowEarlyPayDiscount ? getRowEarlyPayDiscount(row.original) : 0
        if (discount !== 0) {
          acc.has_early_pay_discount = true
          acc.early_pay_discount_total += discount
        }
      } else if (kind === "prepayment") {
        // Both legs, signed — a balanced pair nets to zero and must never read as a deduction.
        acc.prepayments_total += amount
      } else if (amount < 0) {
        acc.deductions_total += amount
        if (getRowDeductionTradeTotals) {
          const trade_totals = getRowDeductionTradeTotals(row.original)
          acc.deduction_trade_totals ??= {
            trade_total: 0,
            non_trade_total: 0,
            unclassified_total: 0,
          }
          acc.deduction_trade_totals.trade_total += trade_totals.trade_total
          acc.deduction_trade_totals.non_trade_total += trade_totals.non_trade_total
          acc.deduction_trade_totals.unclassified_total += trade_totals.unclassified_total
        }
      }
      return acc
    },
    {
      deductions_total: 0,
      invoices_total: 0,
      early_pay_discount_total: 0,
      repayments_total: 0,
      prepayments_total: 0,
      has_repayments: false,
      has_early_pay_discount: false,
    }
  )
}

function has_deduction_trade_total(total: number): boolean {
  return total !== 0
}

function deduction_summary_total(split_totals: AccountingSplitTotals): number {
  return split_totals.deductions_total + split_totals.early_pay_discount_total
}

function accounting_summary_total(total_amount: number, split_totals: AccountingSplitTotals | null): number {
  return total_amount + (split_totals?.early_pay_discount_total ?? 0)
}

function render_amount_summary(
  total_amount: number,
  split_totals: AccountingSplitTotals | null
) {
  if (split_totals) {
    const deduction_trade_totals = split_totals.deduction_trade_totals
    const deduction_total = deduction_summary_total(split_totals)
    const summary_total = accounting_summary_total(total_amount, split_totals)
    if (deduction_total !== 0) {
      return (
        <div className="ml-3 flex flex-1 items-start gap-4">
          <div className="flex flex-col gap-0.5">
            <span>Deductions: {currencyFormatter(deduction_total)}</span>
            {deduction_trade_totals && has_deduction_trade_total(deduction_trade_totals.trade_total) && (
              <span className="pl-4">Trade: {currencyFormatter(deduction_trade_totals.trade_total)}</span>
            )}
            {deduction_trade_totals && has_deduction_trade_total(deduction_trade_totals.non_trade_total) && (
              <span className="pl-4">Non-Trade: {currencyFormatter(deduction_trade_totals.non_trade_total)}</span>
            )}
            {deduction_trade_totals && has_deduction_trade_total(deduction_trade_totals.unclassified_total) && (
              <span className="pl-4">Unclassified: {currencyFormatter(deduction_trade_totals.unclassified_total)}</span>
            )}
            {split_totals.has_early_pay_discount && (
              <span className="pl-4">Early Pay: {currencyFormatter(split_totals.early_pay_discount_total)}</span>
            )}
          </div>
          {split_totals.invoices_total !== 0 && <span>Invoices: {currencyFormatter(split_totals.invoices_total)}</span>}
          {split_totals.has_repayments && <span>Repayments: {currencyFormatter(split_totals.repayments_total)}</span>}
          {split_totals.prepayments_total !== 0 && (
            <span>Prepayments: {currencyFormatter(split_totals.prepayments_total)}</span>
          )}
          <span className="ml-auto">Total: {currencyFormatter(summary_total)}</span>
        </div>
      )
    }
    return (
      <span className="ml-3 flex flex-1 items-center gap-4">
        {split_totals.invoices_total !== 0 && <span>Invoices: {currencyFormatter(split_totals.invoices_total)}</span>}
        {split_totals.has_early_pay_discount && (
          <span>Early Pay Discount: {currencyFormatter(split_totals.early_pay_discount_total)}</span>
        )}
        {split_totals.has_repayments && <span>Repayments: {currencyFormatter(split_totals.repayments_total)}</span>}
        {split_totals.prepayments_total !== 0 && (
          <span>Prepayments: {currencyFormatter(split_totals.prepayments_total)}</span>
        )}
        <span className="ml-auto">Total: {currencyFormatter(summary_total)}</span>
      </span>
    )
  }
  return total_amount !== 0 ? <span className="ml-auto">Total: {currencyFormatter(total_amount)}</span> : null
}

interface PageControlsProps<RowData> {
  table: TTable<RowData>
  getRowAmount?: (row: RowData) => number
  getRowKind?: (row: RowData) => string
  /** Early-pay discount (as a negative reduction) for an invoice row; surfaced separately in the footer. */
  getRowEarlyPayDiscount?: (row: RowData) => number
  /** Signed trade/non-trade deduction amounts; used only when the org exposes the `is_trade` field. */
  getRowDeductionTradeTotals?: (row: RowData) => AccountingDeductionTradeTotals
  /** When true with getRowAmount and getRowKind, show separate accounting totals in the footer. */
  showSplitTotals?: boolean
}

export function PageControls<RowData>({
  table,
  getRowAmount,
  getRowKind,
  getRowEarlyPayDiscount,
  getRowDeductionTradeTotals,
  showSplitTotals = false,
}: PageControlsProps<RowData>) {
  const filteredRows = table.getFilteredRowModel().rows
  const totalAmount = sum_filtered_row_amounts(filteredRows, getRowAmount)
  const splitTotals =
    showSplitTotals && getRowAmount && getRowKind
      ? sum_filtered_accounting_totals(filteredRows, getRowAmount, getRowKind, getRowEarlyPayDiscount, getRowDeductionTradeTotals)
      : null

  return (
    <>
      <div className="flex flex-1 items-start text-sm text-muted-foreground">
        <span className="shrink-0">
          {table.getFilteredSelectedRowModel().rows.length} of {table.getFilteredRowModel().rows.length} row(s) selected.
        </span>
        {render_amount_summary(totalAmount, splitTotals)}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={value => {
              table.setPageSize(Number(value))
            }}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[100, 50, 25, 10].map(pageSize => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}>
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}>
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}>
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}>
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("sum_filtered_row_amounts", () => {
    it("defaults to deduction_amount", () => {
      const rows = [{ original: { deduction_amount: 100 } }, { original: { deduction_amount: 50 } }]
      expect(sum_filtered_row_amounts(rows)).toBe(150)
    })

    it("uses a custom getter when provided", () => {
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100 } },
        { original: { kind: "invoice", invoice_amount: -200 } },
      ]
      expect(
        sum_filtered_row_amounts(rows, row => (row.kind === "invoice" ? Math.abs(row.invoice_amount) : row.invoice_amount))
      ).toBe(100)
    })
  })

  describe("sum_filtered_accounting_totals", () => {
    it("buckets deductions, invoices, and repayments separately", () => {
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100 } },
        { original: { kind: "deduction", invoice_amount: -50 } },
        { original: { kind: "invoice", invoice_amount: -200 } },
        { original: { kind: "repayment", invoice_amount: 75 } },
        { original: { kind: "deduction", invoice_amount: 25 } },
      ]
      const get_amount = (row: { kind: string; invoice_amount: number }) =>
        row.kind === "invoice" ? Math.abs(row.invoice_amount) : row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind

      expect(sum_filtered_accounting_totals(rows, get_amount, get_kind)).toEqual({
        deductions_total: -150,
        invoices_total: 200,
        early_pay_discount_total: 0,
        repayments_total: 75,
        prepayments_total: 0,
        has_repayments: true,
        has_early_pay_discount: false,
      })
    })

    it("omits repayments from totals when none exist", () => {
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100 } },
        { original: { kind: "invoice", invoice_amount: 200 } },
      ]
      const get_amount = (row: { kind: string; invoice_amount: number }) =>
        row.kind === "invoice" ? Math.abs(row.invoice_amount) : row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind

      expect(sum_filtered_accounting_totals(rows, get_amount, get_kind)).toEqual({
        deductions_total: -100,
        invoices_total: 200,
        early_pay_discount_total: 0,
        repayments_total: 0,
        prepayments_total: 0,
        has_repayments: false,
        has_early_pay_discount: false,
      })
    })

    it("separates the early-pay discount from the invoice face value", () => {
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100, discount_amount: 0 } },
        // Early-pay invoices: face value in invoices_total, discount magnitude in the discount bucket.
        { original: { kind: "invoice", invoice_amount: 2000, discount_amount: -40 } },
        { original: { kind: "invoice", invoice_amount: 500, discount_amount: -10 } },
      ]
      const get_amount = (row: { kind: string; invoice_amount: number }) =>
        row.kind === "invoice" ? Math.abs(row.invoice_amount) : row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind
      const get_discount = (row: { discount_amount?: number }) => -Math.abs(row.discount_amount ?? 0)

      expect(sum_filtered_accounting_totals(rows, get_amount, get_kind, get_discount)).toEqual({
        deductions_total: -100,
        invoices_total: 2500,
        early_pay_discount_total: -50,
        repayments_total: 0,
        prepayments_total: 0,
        has_repayments: false,
        has_early_pay_discount: true,
      })
    })

    it("a balanced prepayment pair nets to zero and never reads as a deduction", () => {
      // UNFI `…PP` pairs: withheld on one check (negative), applied on another (positive). The old
      // sign catch-all put the negative legs in deductions_total and dropped the positive legs,
      // showing three balanced pairs as −$48,429.87 of phantom deductions.
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100 } },
        { original: { kind: "prepayment", invoice_amount: -15429.12 } },
        { original: { kind: "prepayment", invoice_amount: 15429.12 } },
      ]
      const get_amount = (row: { kind: string; invoice_amount: number }) => row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind

      const totals = sum_filtered_accounting_totals(rows, get_amount, get_kind)
      expect(totals.deductions_total).toBe(-100)
      expect(totals.prepayments_total).toBe(0)
    })

    it("an open prepayment leg lands in the prepayments bucket, not deductions", () => {
      const rows = [{ original: { kind: "prepayment", invoice_amount: -19543.55 } }]
      const get_amount = (row: { kind: string; invoice_amount: number }) => row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind

      const totals = sum_filtered_accounting_totals(rows, get_amount, get_kind)
      expect(totals.deductions_total).toBe(0)
      expect(totals.prepayments_total).toBe(-19543.55)
    })

    it("includes early-pay discounts in the displayed deduction and summary totals", () => {
      const split_totals: AccountingSplitTotals = {
        deductions_total: -100,
        invoices_total: 2500,
        early_pay_discount_total: -50,
        repayments_total: 75,
        prepayments_total: 0,
        has_repayments: true,
        has_early_pay_discount: true,
      }

      expect(deduction_summary_total(split_totals)).toBe(-150)
      expect(accounting_summary_total(2475, split_totals)).toBe(2425)
    })

    it("separates deduction totals by trade classification when provided", () => {
      const rows = [
        { original: { kind: "deduction", invoice_amount: -100, trade_total: -80, non_trade_total: -20, unclassified_total: 0 } },
        { original: { kind: "deduction", invoice_amount: -50, trade_total: 0, non_trade_total: -30, unclassified_total: -20 } },
        { original: { kind: "invoice", invoice_amount: 200, trade_total: 0, non_trade_total: 0, unclassified_total: 0 } },
      ]
      const get_amount = (row: { kind: string; invoice_amount: number }) =>
        row.kind === "invoice" ? Math.abs(row.invoice_amount) : row.invoice_amount
      const get_kind = (row: { kind: string }) => row.kind
      const get_trade_totals = (row: { trade_total: number; non_trade_total: number; unclassified_total: number }) => ({
        trade_total: row.trade_total,
        non_trade_total: row.non_trade_total,
        unclassified_total: row.unclassified_total,
      })

      expect(sum_filtered_accounting_totals(rows, get_amount, get_kind, undefined, get_trade_totals)).toEqual({
        deductions_total: -150,
        invoices_total: 200,
        early_pay_discount_total: 0,
        repayments_total: 0,
        prepayments_total: 0,
        has_repayments: false,
        has_early_pay_discount: false,
        deduction_trade_totals: {
          trade_total: -80,
          non_trade_total: -50,
          unclassified_total: -20,
        },
      })
    })
  })
}
